import { Button, ButtonGroup } from "@components/button";
import { WithConfirmationDialog, withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { ActionType, addNewLineItem, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    IPrEldpEmployeeEntity,
    IPrEldpEntity,
    IPrEldpItemEntity,
    PrEldpCompanyEntity,
    PrEldpEmployeeEntity,
    PrEldpEntity,
    PrEldpItemEntity
} from "@odata/GeneratedEntityTypes";
import {
    fetchEldpPayrollSettings,
    fetchEmployeeInfo,
    fetchSocialSecurityAdministrations,
    IPrEldpCustomFormData
} from "@pages/payroll/Eldp/Eldp.utils";
import React from "react";

import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { AwaitedArray, toTuple } from "../../../global.types";
import { getUtcDayjs } from "../../../types/Date";
import { IContextInitArgs } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";

class ELDPFormView extends FormViewForExtend<IPrEldpEntity, IFormViewProps<IPrEldpEntity, IPrEldpCustomFormData> & WithConfirmationDialog> {

    async onAfterLoad(): Promise<void> {
        const { storage } = this.props;
        const { bindingContext, entity } = storage.data;

        const isNew = bindingContext.isNew();

        if (isNew) {
            if (!entity.Items || entity.Items.length === 0) {
                const item = addNewLineItem(storage, PrEldpEntity.Items);
                this.recalculateDaysInInterval(bindingContext.navigate(PrEldpEntity.Items).addKey(item))
                        .then(() => storage.refreshFields());
            }

            const company = storage.context.getCompany();

            // set default CSSZ
            const saCode = company?.SocialSecurityCode;
            const socialAdministration = storage.getCustomData()?.governmentInstitutions?.find(item => item.Code === saCode);
            storage.setValueByPath(PrEldpEntity.SocialAdministration, socialAdministration);

            // populate payroll settings to form
            const payrollSettings = storage.getCustomData()?.payrollSetting;
            if (payrollSettings) {
                storage.setValueByPath(createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.SymbolVariable), payrollSettings.CzechSocialSecurityAdministration?.SymbolVariable);
            }
            storage.setValueByPath(createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.LegalName), company.LegalName);
            storage.setValueByPath(createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.LegalNumber), company?.LegalNumber);
            storage.setValueByPath(createPath(PrEldpEntity.EldpCompany, PrEldpCompanyEntity.Company), { Id: company?.Id });
        }

        return super.onAfterLoad();
    }

    getAdditionalLoadPromise(args: IContextInitArgs): (Promise<unknown | void>)[] {
        const { storage } = this.props;
        return toTuple([
            fetchEldpPayrollSettings(storage), fetchSocialSecurityAdministrations(storage),
            ...super.getAdditionalLoadPromise(args)
        ]);
    }

    getAdditionalResults(): AwaitedArray<ReturnType<ELDPFormView["getAdditionalLoadPromise"]>> {
        return this.props.storage.data.additionalResults;
    }

    async recalculateDaysInInterval(bc: BindingContext) {
        const { storage } = this.props;
        const { Year } = storage.data.entity;
        const excludedMonths = storage.getValue(bc.navigate(PrEldpItemEntity.ExcludedMonths)) as string;
        let excludedMonthsArray: number[] = [];
        if (excludedMonths) {
            if (excludedMonths[12] === "1") {
                // if all months are excluded, set days to 0
                storage.setValue(bc.navigate(PrEldpItemEntity.DaysInInterval), 0);
                return;
            } else {
                // remove excluded months from holidays
                excludedMonthsArray = excludedMonths.slice(0, 12).split("")
                        .map((v, i) => v === "1" ? i + 1 : null)
                        .filter(Boolean);
            }
        }
        const item = storage.getValue(bc) as IPrEldpItemEntity;
        if (!!item.DateValidFrom && !!item.DateValidTo) {
            let days = getUtcDayjs(item.DateValidTo).diff(item.DateValidFrom, "days") + 1; // +1 to include the last day
            // subtract excluded months
            excludedMonthsArray.forEach(month => {
                let start = getUtcDayjs().set("year", Year).set("month", month - 1).startOf("month");
                if (start.isBefore(item.DateValidFrom)) {
                    start = getUtcDayjs(item.DateValidFrom);
                }
                let end = getUtcDayjs().set("year", Year).set("month", month - 1).endOf("month");
                if (end.isAfter(item.DateValidTo)) {
                    end = getUtcDayjs(item.DateValidTo);
                }
                if (start.isBefore(end)) {
                    days -= end.diff(start, "days") + 1; // +1 to include the last day of the month
                }
            });
            // set working days in interval
            storage.setValue(bc.navigate(PrEldpItemEntity.DaysInInterval), days);
        }
    }

    handleEmployeeChange = async (e: ISmartFieldChange): Promise<void> => {
        if (e.bindingContext.getPath() === PrEldpEmployeeEntity.Employee && e.triggerAdditionalTasks) {
            // prefill data with current employee data
            const { storage } = this.props;
            const employee = await fetchEmployeeInfo(storage, e.value as number);
            const employeeBc = storage.data.bindingContext.navigate(PrEldpEntity.Employee);

            Object.keys(employee).forEach((path) => {
                const value = employee?.[path as unknown as keyof IPrEldpEmployeeEntity];
                if (value !== undefined) {
                    storage.setValue(employeeBc.navigate(path), value);
                }
            });

            storage.refresh(true);
        }
    }

    handleYearChange = async (e: ISmartFieldChange) => {
        if (e.bindingContext.getPath() === PrEldpEntity.Year && e.triggerAdditionalTasks) {
            const { storage } = this.props;

            const itemsBc = storage.data.bindingContext.navigate(PrEldpEntity.Items);
            const _updateYear = (date: Date): Date => getUtcDayjs(date).set("year", e.value as number).toDate();

            const promises = storage.data.entity.Items.map((item: IPrEldpItemEntity) => {
                // change date of all items to the selected year, if set
                if (item.DateValidFrom) {
                    item.DateValidFrom = _updateYear(item.DateValidFrom);
                }
                if (item.DateValidTo) {
                    item.DateValidTo = _updateYear(item.DateValidTo);
                }

                // recalculate working days
                this.recalculateDaysInInterval(itemsBc.addKey(item));
            });

            await Promise.all(promises);

            this.props.storage.refresh();
        }
    }

    handleChange(e: ISmartFieldChange): void {
        this.props.storage.handleChange(e);

        this.handleEmployeeChange(e);
        this.handleYearChange(e);

        const shouldUpdate = e.triggerAdditionalTasks !== false;
        this.props.storage.refreshFields(shouldUpdate);
    }

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        switch (actionId) {
            default:
                return super.handleCustomHeaderAction(actionId);
        }
    }

    handleLineItemsChange(e: ISmartFieldChange): void {
        const { storage } = this.props;
        storage.handleLineItemsChange(e);

        const path = e.bindingContext.getPath() as PrEldpItemEntity;
        if ([PrEldpItemEntity.DateValidFrom, PrEldpItemEntity.DateValidTo, PrEldpItemEntity.ExcludedMonths].includes(path) ) {
            this.recalculateDaysInInterval(e.bindingContext.getParent())
                    .then(() => storage.refreshFields());
        }
        if ([PrEldpItemEntity.DaysDeducted, PrEldpItemEntity.DaysExcluded, PrEldpItemEntity.CalculationBasis].includes(path)) {
            storage.refreshGroup(e.bindingContext);
        }

        const shouldUpdate = e.triggerAdditionalTasks !== false;
        storage.refreshFields(shouldUpdate);
    }

    handleLineItemsAction = (args: ISmartFastEntriesActionEvent<IPrEldpItemEntity>): void => {
        if (args.actionType === ActionType.Custom) {
            switch (args.customActionType) {

                default:
                    throw new Error(`Unknown custom actionType ${args.customActionType}`);
            }
            this.forceUpdate();
        } else {
            this.props.storage.handleLineItemsAction(args);
            this.props.storage.refresh();
        }
    };

    handleSaveAndPostClick = () => {
        // todo: add POST parameter possibly to save request ??
        this.handleSaveClick();
    }

    // onAfterSave(isNew: boolean, preventTableViewRefresh: boolean) {
    //     super.onAfterSave(isNew, preventTableViewRefresh);
    // }
    //
    // onBeforeSave(data?: IPrPayslipEntity): IPrPayslipEntity {
    //     data = super.onBeforeSave(data) ?? data ?? cloneDeep(this.entity);
    //     return this.clearNotOverridenFields(this._overrideColumnPaths, data);
    // }

    renderButtons(): React.ReactElement {
        const storage = this.props.storage;
        const isFormReadOnly = storage.isReadOnly;
        const isSaveDisabled = this.shouldDisableSaveButton();
        const isCancelDisabled = this.shouldDisableCancelButton();

        return (
                <ButtonGroup wrap={"wrap"}>
                    <Button hotspotId={"formCancel"}
                            isDisabled={storage.isDisabled || isCancelDisabled}
                            onClick={this.handleFormCancel}
                            isTransparent>{storage.t("Common:General.Cancel")}</Button>
                    {this.isDeletable &&
                            <SmartFormDeleteButton storage={storage}
                                                   onClick={this.handleDelete}/>
                    }
                    {this.shouldAllowCopy() && <Button hotspotId={"formCopy"}
                                                       onClick={this.handleCopy}
                                                       isDisabled={this.shouldDisableCopyButton()}
                                                       isTransparent>{storage.t("Common:Form.CreateCopy")}</Button>}
                    {!isFormReadOnly && (<>
                            <Button hotspotId={"formSaveWithoutPosting"}
                                    onClick={this.handleSaveClick}
                                    isDisabled={isSaveDisabled}
                                    isTransparent>{storage.t("Eldp:Form.SaveWithoutPosting")}</Button>
                            <Button hotspotId={"formSave"}
                                    onClick={this.handleSaveAndPostClick}
                                    isDisabled={isSaveDisabled}>
                                {storage.t("Eldp:Form.SaveAndPost")}
                            </Button>
                    </>)}
                </ButtonGroup>
        );
    }
}

export default withPermissionContext(withConfirmationDialog(ELDPFormView));